import { useMemo } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { useAtomValue } from 'jotai';

import { selectedPlanAtom } from '../../atoms';

import { BenefitsTable } from './common/BenefitsTable';

export const CoverageSummarySection = () => {
  // atoms
  const currentPlan = useAtomValue(selectedPlanAtom);

  // hooks
  const { t } = useLingui();

  // Transform Zuub coverage data into table format
  const coverageData = useMemo(() => {
    // Only show data if we have a valid selected plan with a plan type
    if (!currentPlan || !currentPlan.plan?.planType || !currentPlan?.coverage) {
      return { leftTableData: [], rightTableData: [], isValid: false };
    }

    const allServices: Array<{
      category: string;
      adaCode: string;
      coverage: number;
    }> = [];

    // Flatten all coverage categories and their services
    currentPlan.coverage.forEach((coverage) => {
      // Only include categories that have services
      if (coverage.services.length > 0) {
        coverage.services.forEach((service) => {
          allServices.push({
            category: coverage.categoryName,
            adaCode: service.adaCode,
            coverage: service.planCoverage,
          });
        });
      }
    });

    // Split into two tables (roughly equal distribution)
    const midPoint = Math.ceil(allServices.length / 2);
    const leftTableData = allServices.slice(0, midPoint);
    const rightTableData = allServices.slice(midPoint);

    return { leftTableData, rightTableData, isValid: true };
  }, [currentPlan]);

  // Only show data if we have a valid selected plan with a plan type
  if (!coverageData.isValid) {
    return (
      <div className="vai-text-center vai-text-gray-500 vai-py-8 vai-bg-gray-50 vai-rounded-lg">
        <Trans>No coverage data available.</Trans>
      </div>
    );
  }

  const planType = currentPlan!.plan!.planType!.toLowerCase();

  const columns = [
    { header: <Trans>Category</Trans>, align: 'left' as const },
    { header: <Trans>ADA Codes</Trans>, align: 'center' as const },
    { header: planType.toUpperCase(), align: 'center' as const },
  ];

  const createTableRows = (data: typeof coverageData.leftTableData) =>
    data.map((service, index) => ({
      key: `${service.category}-${service.adaCode}-${index}`,
      cells: [service.category, service.adaCode, `${service.coverage}%`],
    }));

  // Check if both tables would be empty
  const totalServices =
    coverageData.leftTableData.length + coverageData.rightTableData.length;

  if (totalServices === 0) {
    return (
      <div className="vai-text-center vai-text-gray-500 vai-py-8 vai-bg-gray-50 vai-rounded-lg">
        <Trans>No coverage data available.</Trans>
      </div>
    );
  }

  // If there's only one row, show a single full-width table
  if (totalServices === 1) {
    return (
      <div className="vai-space-y-6">
        <BenefitsTable
          columns={columns}
          rows={createTableRows(coverageData.leftTableData)}
          emptyMessage={t`No coverage data available.`}
        />
      </div>
    );
  }

  return (
    <div className="vai-space-y-6">
      {/* Two tables side by side */}
      <div className="vai-grid vai-grid-cols-2 vai-gap-8">
        <BenefitsTable
          columns={columns}
          rows={createTableRows(coverageData.leftTableData)}
          emptyMessage={t`No coverage data available.`}
        />
        <BenefitsTable
          columns={columns}
          rows={createTableRows(coverageData.rightTableData)}
        />
      </div>
    </div>
  );
};
